/**
 * The day's weather.
 *
 * Deliberately a *simulation* concern rather than a renderer flourish: the
 * weather is drawn from the seeded RNG at each day boundary, so it is part of
 * the save, identical for a given seed, and available to headless runs. That
 * keeps the door open for it to matter mechanically later - rain suppressing
 * yard demand is the obvious one - without a second source of truth appearing
 * in the client.
 *
 * For this build it is presentation only. Nothing in the economy reads it yet,
 * and that is recorded rather than hidden: see PROJECT_STATE.
 */
import type { GameState } from '../state/GameState.js';

export type Weather = 'sunny' | 'cloudy' | 'light_rain';

/**
 * Weighted so the neighbourhood is mostly pleasant. A game whose first
 * impression is half a chance of rain is a game about rain.
 */
const TABLE: Weather[] = [
  'sunny', 'sunny', 'sunny', 'sunny', 'sunny',
  'cloudy', 'cloudy', 'cloudy',
  'light_rain', 'light_rain',
];

export const WEATHER_LABELS: Record<Weather, string> = {
  sunny: 'Clear',
  cloudy: 'Overcast',
  light_rain: 'Light rain',
};

export class WeatherSystem {
  /**
   * Set the weather for the state's current day.
   *
   * Derived from the seed and the day number rather than drawn from the shared
   * RNG. Drawing from the shared stream would shift every later draw in the
   * simulation - which leads appear, which applicants turn up, how an
   * employee's day goes - so adding weather would silently rebalance the game.
   * A pure function of (seed, day) has none of that reach and needs no state.
   */
  roll(state: GameState): Weather {
    state.weather = forDay(state.seed, state.time.day);
    return state.weather;
  }

  /** Never trust a loaded save to carry a field this young. */
  current(state: GameState): Weather {
    return state.weather ?? forDay(state.seed, state.time.day);
  }
}

/** Deterministic weather for one day of one game. */
export function forDay(seed: number, day: number): Weather {
  // A small integer hash: the day number alone would give every seed the same
  // week, and seed alone would give one game one weather forever.
  let h = (seed ^ (day * 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return TABLE[h % TABLE.length]!;
}
